import { useDispatch } from 'react-redux';

import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { store } from '@core/store';
import { updateDownloadStatus } from '@features/downloads/downloadsSlice';
import { JobStatusEnum } from '@features/lead-management/constants/lead-report';
import {
  useLazyGetDownloadFileStatusQuery,
  useLazyGetLeadReportJobStatusQuery
} from '@features/reports/queries/ReportsQuery';
import useNotify from '@hooks/notify';
import { DownloadItem } from 'types/downloads';

import { downloadFileFromUrlUsingBlob } from './downloadFile';
import { getLocalStorageItem } from './storage';

interface PollStatusParams {
  jobId: string;
  downloadId: string;
  mixpanelData?: Record<string, any>;
}

const usePollDownloadStatus = () => {
  const dispatch = useDispatch();
  const { createErrorAlert } = useNotify();
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const [getDownloadFileStatus] = useLazyGetDownloadFileStatusQuery();
  const [getLeadReportJobStatus] = useLazyGetLeadReportJobStatusQuery();

  const pollDownloadStatus = async ({
    jobId,
    downloadId,
    mixpanelData
  }: PollStatusParams): Promise<void> => {
    let pollCount = 0;
    const maxInitialPolls = 5; // First 5 seconds with 1-second intervals
    const initialPollInterval = 1000; // 1 seconds
    const subsequentPollInterval = 5000; // 5 seconds
    const maxTotalPolls = 60; // Stop after 5 minutes (60 * 5 seconds)
    const startTime = Date.now();
    const maxDuration = 5 * 60 * 1000; // 5 minutes in milliseconds

    const pollStatus = async (): Promise<void> => {
      if (pollCount >= maxTotalPolls || Date.now() - startTime > maxDuration) {
        dispatch(
          updateDownloadStatus({
            id: downloadId,
            status: 'timeout',
            error: 'Report generation timed out'
          })
        );
        createErrorAlert('Report generation timed out. Please try again.');
        return;
      }

      // check if the download is already completed or cancelled
      const currentState = store.getState();
      const currentDownloads = currentState.downloads.items;
      const download = currentDownloads.find(
        (d: DownloadItem) => d.id === downloadId
      );
      if (download && download.status !== 'started') {
        return;
      }

      try {
        const { data } = await getDownloadFileStatus({
          jobId
        });

        if (data) {
          if (data.status === 'success' || data.status === 'completed') {
            // Download file from the URL
            if (data.result?.file_url) {
              await downloadFileFromUrlUsingBlob(data.result.file_url);
            }

            dispatch(
              updateDownloadStatus({
                id: downloadId,
                status: 'completed'
              })
            );

            if (mixpanelData) {
              sendMixpanelEvent(Event.DOWNLOAD_REPORT_SUCCESS, mixpanelData);
            }

            return; // Stop polling
          }
          if (data.status === 'error') {
            dispatch(
              updateDownloadStatus({
                id: downloadId,
                status: 'error',
                error: 'Report generation failed'
              })
            );

            if (mixpanelData) {
              sendMixpanelEvent(Event.DOWNLOAD_REPORT_FAILED, mixpanelData);
            }

            createErrorAlert(data?.error || 'Error generating report');
            return; // Stop polling
          }
          if (data.status === 'in_progress' || data.status === 'pending') {
            // Continue polling
            pollCount += 1;

            if (pollCount < maxInitialPolls) {
              // Poll every 1 second for first 5 seconds
              setTimeout(pollStatus, initialPollInterval);
            } else {
              // Poll every 5 seconds after first 5 seconds
              setTimeout(pollStatus, subsequentPollInterval);
            }
          }
        }
      } catch (error) {
        dispatch(
          updateDownloadStatus({
            id: downloadId,
            status: 'error',
            error: 'Failed to check report status'
          })
        );

        createErrorAlert('Error checking report status');
      }
    };

    // Start polling
    pollStatus();
  };

  const pollLeadReportJobStatus = async ({
    jobId,
    downloadId
  }: PollStatusParams): Promise<void> => {
    let pollCount = 0;
    const maxInitialPolls = 5; // First 5 seconds with 1-second intervals
    const initialPollInterval = 1000; // 1 seconds
    const subsequentPollInterval = 5000; // 5 seconds
    const maxTotalPolls = 60; // Stop after 5 minutes (60 * 5 seconds)
    const startTime = Date.now();
    const maxDuration = 5 * 60 * 1000; // 5 minutes in milliseconds

    const pollStatus = async (): Promise<void> => {
      if (pollCount >= maxTotalPolls || Date.now() - startTime > maxDuration) {
        dispatch(
          updateDownloadStatus({
            id: downloadId,
            status: 'timeout',
            error: 'Report generation timed out'
          })
        );
        createErrorAlert('Report generation timed out. Please try again.');
        return;
      }

      // check if the download is already completed or cancelled
      const currentState = store.getState();
      const currentDownloads = currentState.downloads.items;
      const download = currentDownloads.find(
        (d: DownloadItem) => d.id === downloadId
      );
      if (download && download.status !== 'started') {
        return;
      }

      try {
        const { data } = await getLeadReportJobStatus({
          jobId,
          organizationId: organizationId || ''
        });
        if (data) {
          if (data.status === JobStatusEnum.SUCCESS) {
            if (data.signed_url) {
              // Download the file directly without opening a new tab
              await downloadFileFromUrlUsingBlob(data.signed_url);
            }

            dispatch(
              updateDownloadStatus({
                id: downloadId,
                status: 'completed'
              })
            );
            return; // Stop polling
          }
          if (data.status === JobStatusEnum.FAILED) {
            dispatch(
              updateDownloadStatus({
                id: downloadId,
                status: 'error',
                error: 'Report generation failed'
              })
            );

            createErrorAlert(data?.error || 'Error generating report');
            return; // Stop polling
          }
          if (
            data.status === JobStatusEnum.PROCESSING ||
            data.status === JobStatusEnum.PENDING
          ) {
            // Continue polling
            pollCount += 1;

            if (pollCount < maxInitialPolls) {
              // Poll every 1 second for first 5 seconds
              setTimeout(pollStatus, initialPollInterval);
            } else {
              // Poll every 5 seconds after first 5 seconds
              setTimeout(pollStatus, subsequentPollInterval);
            }
          }
        }
      } catch (error) {
        dispatch(
          updateDownloadStatus({
            id: downloadId,
            status: 'error',
            error: 'Failed to check report status'
          })
        );

        createErrorAlert('Error checking report status');
      }
    };

    // Start polling
    pollStatus();
  };
  return { pollDownloadStatus, pollLeadReportJobStatus };
};
export default usePollDownloadStatus;
